import {
  Box,
  Button,
  CircularProgress,
  Typography,
  IconButton,
  InputAdornment,
  Link,
  TextField,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useMsal } from "@azure/msal-react";
import dayjs from "dayjs";
import { useContext, useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import logo from "../../assets/Images/sigmasoft.png";
import { UserManagentContext } from "../../customHooks/dataProviders/UserManagementProvider";
import { validatePassword } from "../../common/commonValidation/CommonValidation";
import { Environment } from "../../environments/Environment";
import {
  GetRoleNamesByUsername,
  ResetPwdWithOtp,
  SendOtp,
  ssoMicrosoftLogin,
} from "../../service/api/login/loginService";

const LoginPage = () => {
  const navigate = useNavigate();
  const [panel, setPanel] = useState("Login");
  const [showPassword, setShowPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isSsoLoading, setIsSsoLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formdata, setFormdata] = useState({
    userName: "",
    password: "",
    confirmPassword: "",
    otp: "",
  });
  const { setuserManagementData } = useContext(UserManagentContext);
  const { instance, accounts, inProgress } = useMsal();

  const handlechange = (e) => {
    const { name, value } = e.target;
    if (name === "otp" && (isNaN(value) || value.length > 6)) return;
    setFormdata({ ...formdata, [name]: value });
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const handleForgotClick = () => {
    if (panel === "Login") setPanel("Send OTP");
    else setPanel("Login");
  };

  const handleLogin = async () => {
    if (!formdata.userName || !formdata.password) {
      setErrorMessage("Please fill all the fields");
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(`${Environment.loginUrl}/apiiLogin`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          username: formdata.userName,
          password: formdata.password,
        }),
      });

      if (!response.ok) {
        throw new Error(`Server Error: ${response.status}`);
      }

      const data = await response.json();

      if (data.status !== "OK") {
        setErrorMessage("Invalid Username or Password");
      } else {
        const { userId, jwtToken, roles } = data;
        localStorage.setItem("jwtToken", jwtToken);
        localStorage.setItem(
          "expiryTime",
          dayjs().add(30000000, "milliseconds")
        );
        const user = { email: formdata.userName, roles: roles || [] };
        localStorage.setItem("userId", userId);
        localStorage.setItem("userMailId", formdata.userName);

        GetRoleNamesByUsername(formdata.userName)
          .then((roleNames) => {
            setuserManagementData({ roleTypes: roles, roleNames });
            localStorage.setItem("roleNames", JSON.stringify(roleNames));
            navigate("/home", { state: { user } });
          })
          .catch((error) => {
            console.error("Error fetching role names:", error);
            setErrorMessage("Error fetching role names");
            navigate("/home", { state: { user } });
          });
      }
    } catch (error) {
      console.error("Login Error:", error.message);
      setErrorMessage("Invalid Username or Password");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendOtp = () => {
    setIsLoading(true);
    SendOtp(formdata.userName)
      .then((data) => {
        if (data) {
          setPanel("Reset Password");
          setIsLoading(false);
        } else {
          setErrorMessage("Error sending OTP");
          setIsLoading(false);
        }
      })
      .catch((error) => {
        setErrorMessage("Something went wrong");
        console.error("Error:", error);
        setIsLoading(false);
      });
  };

  const handleVerifyOtp = () => {
    if (!formdata.otp || !formdata.password || !formdata.confirmPassword) {
      setErrorMessage("Please fill all the fields");
      return;
    }
    if (formdata.password !== formdata.confirmPassword) {
      setErrorMessage("Passwords do not match");
      return;
    }
    if (!validatePassword(formdata.password)) {
      setErrorMessage(
        "Password must be at least 8 characters, include a lowercase, uppercase, number, and special character."
      );
      return;
    }
    setIsLoading(true);
    ResetPwdWithOtp({
      otp: formdata.otp,
      email: formdata.userName,
      password: formdata.password,
    })
      .then((data) => {
        if (data.trim() === "Invalid or expired OTP.") {
          setErrorMessage("Invalid OTP or error resetting password");
          setIsLoading(false);
        } else {
          setErrorMessage("Password reset successful");
          setIsLoading(false);
          setPanel("Login");
        }
      })
      .catch((error) => {
        setErrorMessage("Error resetting password");
        setIsLoading(false);
      });
  };

  const handleButtonClick = () => {
    switch (panel) {
      case "Login":
        handleLogin();
        break;
      case "Send OTP":
        handleSendOtp();
        break;
      case "Reset Password":
        handleVerifyOtp();
        break;
      default:
        break;
    }
  };


  useEffect(() => {
    if (inProgress !== "none") {
      setIsSsoLoading(true);
    } else {
      setIsSsoLoading(false);
    }
  }, [inProgress]);


  const handleMicrosoftLogin = async () => {
    setIsSsoLoading(true);
    setErrorMessage("");
    // Clear flags so that a manual click allows a fresh attempt
    sessionStorage.removeItem("sso_redirect_attempted");
    sessionStorage.removeItem("sso_sync_attempted");

    try {
      await instance.loginRedirect({
        scopes: ["openid", "profile", "email", "User.Read"],
        prompt: "select_account"
      });
    } catch (error) {
      console.error("Microsoft login error:", error);
      setErrorMessage(
        error.message || "Microsoft login failed. Please try again."
      );
      setIsSsoLoading(false);
    }
  };

  const forgotPasswordInputs = (i) => (
    <TextField
      label="Username"
      variant="outlined"
      size="small"
      fullWidth
      type="email"
      value={i?.userName}
      name="userName"
      onChange={handlechange}
      InputLabelProps={{ shrink: true }}
    />
  );

  const sendOtpInputs = (i) => (
    <>
      <TextField
        label="Username"
        variant="outlined"
        size="small"
        fullWidth
        type="email"
        value={i?.userName}
        name="userName"
        onChange={handlechange}
        inputProps={{
          readOnly: true,
        }}
        InputLabelProps={{ shrink: true }}
      />
      <TextField
        label="Enter OTP"
        placeholder="_ _ _ _ _ _"
        variant="outlined"
        fullWidth
        required
        size="small"
        value={i?.otp}
        name="otp"
        onChange={handlechange}
      />
      <TextField
        label="Password"
        variant="outlined"
        size="small"
        fullWidth
        type="password"
        value={i?.password}
        onChange={handlechange}
        name="password"
      />
      <TextField
        label="Confirm Password"
        variant="outlined"
        size="small"
        fullWidth
        type={showPassword ? "text" : "password"}
        value={i?.confirmPassword}
        onChange={handlechange}
        name="confirmPassword"
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label="toggle password visibility"
                onClick={handleClickShowPassword}
                onMouseDown={handleMouseDownPassword}
                edge="end"
              >
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
    </>
  );

  const loginInputs = (i) => (
    <>
      <TextField
        label="Username"
        variant="outlined"
        size="small"
        fullWidth
        type="email"
        value={i?.userName}
        onChange={handlechange}
        name="userName"
        autoComplete="off"
        InputLabelProps={{ shrink: true }}
      />
      <TextField
        label="Password"
        variant="outlined"
        size="small"
        fullWidth
        type={showPassword ? "text" : "password"}
        value={i?.password}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label="toggle password visibility"
                onClick={handleClickShowPassword}
                onMouseDown={handleMouseDownPassword}
                edge="end"
              >
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
        InputLabelProps={{ shrink: true }}
        onChange={handlechange}
        name="password"
        autoComplete="off"
      />
    </>
  );

  useEffect(() => {
    if (panel === "Reset Password") {
      setFormdata((prev) => ({ ...prev, password: "" }));
    } else if (panel === "Login") {
      setFormdata({
        userName: "",
        password: "",
        otp: "",
        confirmPassword: "",
      });
    }
  }, [panel]);

  useEffect(() => {
    if (errorMessage) {
      const timer = setTimeout(() => {
        setErrorMessage(null);
      }, 8000);
      return () => clearTimeout(timer);
    }
  }, [errorMessage]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        justifyContent: "center",
        alignItems: "center",
        gap: "1rem",
        height: "100vh",
        width: "100%",
        "@media (max-width: 850px)": {
          padding: "0.5rem",
          flexDirection: "column",
        },
        padding: "1rem",
      }}
    >
      <Box
        sx={{
          width: "100%",
          borderRight: "2px solid #ccc",
          paddingRight: "1rem",
          "@media (max-width: 850px)": {
            borderRight: "none",
          },
        }}
      >
        <Box
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "0.3rem",
            alignItems: "center",
          }}
        >
          <img
            src={logo}
            alt="Company Logo"
            style={{ maxWidth: "100%", height: "auto" }}
          />
          <Box
            style={{
              width: "100%",
              display: "flex",
              justifyContent: "end",
              alignItems: "end",
              gap: "0.5rem",
            }}
          >
            <Typography
              variant="body1"
              sx={{
                marginBottom: "0.2rem",
                fontWeight: "bold",
              }}
            >
              Welcome to
            </Typography>
            <Typography
              variant="h4"
              style={{ fontWeight: "bold" }}
              color="primary.main"
            >
              Sigma<span style={{ color: "#FE9900" }}>360</span>
            </Typography>
          </Box>
        </Box>
      </Box>
      <Box sx={{ width: "100%" }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "1rem",
            alignItems: "center",
            justifyContent: "center",
            width: "60%",
            margin: "auto",
            border: "1px solid #ccc",
            padding: "2rem",
            backgroundColor: "#f9f9f9",
            borderRadius: "5px",
            "@media (max-width: 820px)": {
              width: "50%",
              padding: "1rem",
              gap: "0.5rem",
            },
            "@media (max-width: 600px)": {
              padding: "1rem",
              gap: "0.5rem",
              width: "100%",
            },
          }}
        >
          {isSsoLoading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              <CircularProgress color="primary" />
              <Typography variant="h6">Signing in with Microsoft...</Typography>
            </Box>
          ) : (
            <>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "1rem",
                  width: "100%",
                  maxWidth: "400px",
                  "@media (max-width: 600px)": {
                    maxWidth: "100%",
                  },
                }}
              >
                {panel === "Login" && loginInputs(formdata)}
                {panel === "Send OTP" && forgotPasswordInputs(formdata)}
                {panel === "Reset Password" && sendOtpInputs(formdata)}
              </Box>

              {errorMessage && (
                <Box>
                  <Typography
                    sx={{
                      textAlign: "center",
                      mb: 2,
                      color: errorMessage === "Password reset successful" ? "success.main" : "error.main"
                    }}
                    variant="body1"
                  >
                    {errorMessage}
                  </Typography>
                </Box>
              )}

              <Box
                sx={{
                  width: "100%",
                  maxWidth: "400px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "1rem",
                  "@media (max-width: 600px)": { maxWidth: "100%" },
                }}
              >
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  onClick={handleButtonClick}
                  disabled={isLoading}
                  sx={{ py: 1.2 }}
                >
                  {isLoading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    panel
                  )}
                </Button>

                {panel === "Login" && (
                  <>
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', my: 1 }}>
                      <Box sx={{ flex: 1, height: '1px', bgcolor: '#ccc' }} />
                      <Typography sx={{ px: 2, color: 'text.secondary', fontSize: '0.8rem' }}>OR</Typography>
                      <Box sx={{ flex: 1, height: '1px', bgcolor: '#ccc' }} />
                    </Box>

                    <Button
                      variant="outlined"
                      fullWidth
                      onClick={handleMicrosoftLogin}
                      sx={{
                        textTransform: "none",
                        borderColor: "#2F2F2F",
                        color: "#2F2F2F",
                        py: 1.2,
                        fontSize: "0.9rem",
                        "&:hover": {
                          borderColor: "#0078D4",
                          backgroundColor: "rgba(0, 120, 212, 0.04)",
                        },
                      }}
                      startIcon={
                        <img
                          src="https://learn.microsoft.com/en-us/entra/identity-platform/media/howto-add-branding-in-apps/ms-symbollockup_mssymbol_19.png"
                          alt="Microsoft"
                          style={{ width: 20, height: 20 }}
                        />
                      }
                    >
                      Sign in with Microsoft
                    </Button>
                  </>
                )}

                <Link
                  variant="body2"
                  sx={{ textAlign: "center", cursor: "pointer", mt: 1 }}
                  onClick={handleForgotClick}
                  color="error.main"
                  underline="hover"
                >
                  {panel === "Login" ? "Forgot Password?" : "Back to Login"}
                </Link>
              </Box>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default LoginPage;
